import {useAsync2} from "nate-react-api-helpers";
import {useMemo} from "react";
import {Alert, IconButton, LinearProgress, Tooltip} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {css} from "@emotion/css";
import moment from "moment";
import {ChangesetItem} from "../../../../../api/ProjectChangeset";
import {api} from "../../../../../api/API";
import {useProjectId} from "../../ProjectName";

export function TimelineViewer(props: {
    openings?: number[];
    hardwareGroups?: number[];
    value: ChangesetItem | undefined;
    onChange: (value: ChangesetItem | undefined) => void;
}) {
    const projectId = useProjectId();
    const {openings, hardwareGroups, value, onChange} = props;

    const req = useAsync2(
        (input) => api.projectChangeset.listForEntities(input),
        {project: projectId, openings, hardwareGroups},
        [projectId, openings, hardwareGroups]
    );

    const {ordered, touchedIds} = useMemo(() => {
        const result = req.result;
        if (!result) return {ordered: [] as ChangesetItem[], touchedIds: new Set<number>()};

        const seen = new Set<number>();
        const all: ChangesetItem[] = [];
        for (const item of [...result.touched, ...result.others]) {
            if (seen.has(item.id)) continue;
            seen.add(item.id);
            all.push(item);
        }

        all.sort((a, b) => {
            const aTime = new Date(a.createdAt).getTime();
            const bTime = new Date(b.createdAt).getTime();
            if (aTime !== bTime) return bTime - aTime;
            return b.projSeqNumber - a.projSeqNumber;
        });

        const touched = new Set<number>(result.touched.map(t => t.id));
        return {ordered: all, touchedIds: touched};
    }, [req.result]);

    const touchedOrdered = useMemo(
        () => ordered.filter(o => touchedIds.has(o.id)),
        [ordered, touchedIds]
    );

    const currentTouchedIndex = useMemo(() => {
        if (!value) return -1;
        return touchedOrdered.findIndex(o => o.id === value.id);
    }, [touchedOrdered, value]);

    const goPrev = () => {
        if (value === undefined) return;
        if (currentTouchedIndex <= 0) {
            onChange(undefined);
            return;
        }
        onChange(touchedOrdered[currentTouchedIndex - 1]);
    };

    const goNext = () => {
        if (touchedOrdered.length === 0) return;
        if (value === undefined) {
            onChange(touchedOrdered[0]);
            return;
        }
        if (currentTouchedIndex >= touchedOrdered.length - 1) return;
        onChange(touchedOrdered[currentTouchedIndex + 1]);
    };

    if (req.loading) {
        return <LinearProgress variant="indeterminate" />;
    }

    if (req.error) {
        return <Alert severity="error">{req.error}</Alert>;
    }

    if (ordered.length === 0) {
        return <Alert severity="info">No changes found for the selected openings.</Alert>;
    }

    return (
        <div className={styles.wrapper}>
            <IconButton
                onClick={goPrev}
                disabled={value === undefined}
                size="large"
            >
                <ChevronLeftIcon />
            </IconButton>
            <div className={styles.track}>
                <Tooltip title="Current (live state)">
                    <button
                        type="button"
                        className={[
                            styles.tick,
                            value === undefined ? styles.tickSelected : "",
                        ].join(" ")}
                        onClick={() => onChange(undefined)}
                    >
                        <span className={styles.tickLabel}>Current</span>
                        <span className={styles.tickDate}>live</span>
                    </button>
                </Tooltip>
                {ordered.map(item => {
                    const isTouched = touchedIds.has(item.id);
                    const isSelected = value?.id === item.id;
                    return (
                        <Tooltip
                            key={item.id}
                            title={
                                <div>
                                    <div>
                                        #{item.projSeqNumber}
                                        {item.freeformChange ? ` (FF #${item.freeformChange})` : ""}
                                        {" "}{item.name || "(untitled)"}
                                    </div>
                                    <div>{moment(item.createdAt).format("MMM D, YYYY [at] h:mma")}</div>
                                    <div>by {item.createdByName}</div>
                                    {!isTouched && <div style={{opacity: 0.7}}>(does not affect selected openings)</div>}
                                </div>
                            }
                        >
                            <button
                                type="button"
                                className={[
                                    styles.tick,
                                    isSelected ? styles.tickSelected : "",
                                    !isTouched ? styles.tickUntouched : "",
                                ].join(" ")}
                                onClick={() => {
                                    if (!isTouched) return;
                                    onChange(item);
                                }}
                                disabled={!isTouched}
                            >
                                <span className={styles.tickLabel}>
                                    #{item.projSeqNumber}
                                    {item.freeformChange ? ` (FF #${item.freeformChange})` : ""}
                                </span>
                                <span className={styles.tickDate}>
                                    {moment(item.createdAt).format("MMM D")}
                                </span>
                            </button>
                        </Tooltip>
                    );
                })}
            </div>
            <IconButton
                onClick={goNext}
                disabled={
                    touchedOrdered.length === 0 ||
                    (value !== undefined && currentTouchedIndex >= touchedOrdered.length - 1)
                }
                size="large"
            >
                <ChevronRightIcon />
            </IconButton>
        </div>
    );
}

const styles = {
    wrapper: css({
        display: "flex",
        alignItems: "center",
        padding: "8px 16px",
        borderTop: "1px solid #e0e0e0",
        background: "#fafafa",
    }),
    track: css({
        flex: 1,
        display: "flex",
        alignItems: "stretch",
        gap: 4,
        overflowX: "auto",
        padding: "4px 8px",
    }),
    tick: css({
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 64,
        padding: "6px 8px",
        border: "1px solid #c5cae9",
        borderRadius: 4,
        background: "#fff",
        color: "#1a237e",
        cursor: "pointer",
        fontSize: 12,
        lineHeight: 1.3,
        "&:hover:not(:disabled)": {
            background: "#e8eaf6",
        },
    }),
    tickSelected: css({
        background: "#1a237e !important",
        color: "#fff !important",
        borderColor: "#1a237e !important",
        boxShadow: "0 0 0 2px rgba(26,35,126,0.25)",
    }),
    tickUntouched: css({
        opacity: 0.4,
        cursor: "not-allowed",
        color: "#666",
        borderColor: "#e0e0e0",
        background: "#f5f5f5",
    }),
    tickLabel: css({
        fontWeight: 600,
    }),
    tickDate: css({
        fontSize: 11,
        opacity: 0.85,
    }),
};
